// The estate's network index, loaded ONCE per session — the I/O half of
// core/devices/siteGuess (which holds the rules and is pure).
//
// One read of `GetDeviceTrackingList` (~1800 rows, half a second) answers every
// trace: it is the only bulk reply that carries `WifiName`, `DeviceIp` and
// `Bssid` alongside the location, the owner and the user-mapping count.
// ⚠️ ONE READ, NOT ONE PER DEVICE. The Message Box names everything it hears,
// so a per-device lookup would be a request per line — the mistake
// `deviceLabels` already had to design around.
//
// ⚠️ IT NEEDS THE BACKEND'S NEW COLUMNS (`deviceTracking.js`, 2026-10-24).
// Without them every row has an empty SSID, `ready` is false and the hint bar
// says the server needs a restart rather than silently guessing nothing.

import { getDeviceTrackingList } from '../api/api';
import {
  buildNetworkIndex, isUnassignedRow, subjectFrom, guessSentence,
} from './siteGuess';

let loading = null;
let state = null;

const rowsOf = (res) => (Array.isArray(res?.data) ? res.data : []);

/** Load (or reuse) the index. -> { rows, byUno, index, ready, error } */
export function loadSiteIndex({ force = false } = {}) {
  if (force) { loading = null; state = null; }
  if (state) return Promise.resolve(state);
  if (loading) return loading;
  loading = getDeviceTrackingList()
    .then((res) => {
      const rows = rowsOf(res);
      const byUno = new Map(rows.map((r) => [String(r.UniqueNumber), r]));
      state = {
        rows,
        byUno,
        index: buildNetworkIndex(rows),
        // Has the server been restarted with the network columns?
        ready: rows.some((r) => String(r.WifiName || '').trim()),
        error: '',
      };
      return state;
    })
    .catch((e) => {
      loading = null;
      return {
        rows: [], byUno: new Map(), index: buildNetworkIndex([]), ready: false, error: e?.message || 'could not read the device list',
      };
    });
  return loading;
}

export const resetSiteIndex = () => { loading = null; state = null; };

/** The loaded index, or null when it has not been read yet. Synchronous. */
export const siteIndexNow = () => state;

/**
 * The guess for one unique number, from the loaded index plus (optionally) the
 * device's own live boot line. -> { uno, row, unassigned, guess } | null
 *
 * ⚠️ ONLY FOR AN UNASSIGNED DEVICE. A device that already has an owner needs no
 * guessing, and offering one would invite re-assigning somebody's switch.
 */
export function siteHintFor(uno, payload) {
  if (!state) return null;
  const key = String(uno || '').trim();
  if (!key) return null;
  const row = state.byUno.get(key) || null;
  // No row at all is as unassigned as a device can be.
  const unassigned = row ? isUnassignedRow(row) : true;
  if (!unassigned) return null;
  const subject = subjectFrom({ uno: key, row, payload });
  if (!subject) return null;
  return {
    uno: key, row, unassigned, subject, guess: guessSentence(subject, state.index),
  };
}

const siteGuessIndex = {
  loadSiteIndex, resetSiteIndex, siteIndexNow, siteHintFor
};
export default siteGuessIndex;
